import { defineStoryblok } from "@trydig/storyblok";
import { rpc } from "#rpc";
import {
	AccordionBlok,
	AccordionGroupItemBlok,
	AccordionGroupsBlok,
	AccordionItemBlok,
} from "@/components/bloks/Accordion";
import { ArticleCreditsBlok } from "@/components/bloks/ArticleCreditsBlok";
import { ArticlePageBlok } from "@/components/bloks/ArticlePageBlok";
import { ButtonBlok } from "@/components/bloks/ButtonBlok";
import { CampaignCardBlok } from "@/components/bloks/CampaignCardBlok";
import { CarouselEndContentBlok } from "@/components/bloks/CarouselEndContentBlok";
import { EmbedBlok } from "@/components/bloks/EmbedBlok";
import {
	HeroBlok1,
	HeroBlok2,
	HeroBlok3,
	HeroBlok4,
	HeroBlokFullVideo,
} from "@/components/bloks/HeroBlok";
import { IntroBlok } from "@/components/bloks/IntroBlok";
import { LinkBlok } from "@/components/bloks/LinkBlok";
import {
	ManualBlok,
	ManualSectionBlok,
	ManualsBlok,
} from "@/components/bloks/Manual";
import { MediaBlok } from "@/components/bloks/MediaBlok";
import { MediaFrameBlok } from "@/components/bloks/MediaFrameBlok";
import { PageBlok } from "@/components/bloks/PageBlok";
import {
	ProductGridBlok,
	ProductGridBlokSkeleton,
} from "@/components/bloks/ProductGridBlok";
import { ProductSectionsBlok } from "@/components/bloks/ProductSectionsBlok";
import {
	ProductsBlok,
	ProductsSkeleton,
} from "@/components/bloks/ProductsBlok";
import { QouteBlok } from "@/components/bloks/QouteBlok";
import { RetailerListingBlok } from "@/components/bloks/RetailerListingBlok";
import { RichTextBlok } from "@/components/bloks/RichTextBlok";
import { RightOfWithdrawalBlok } from "@/components/bloks/RightOfWithdrawalBlok";
import { StoryCardBlok } from "@/components/bloks/StoryCardBlok";
import { TestimonialBlok } from "@/components/bloks/TestimonialBlok";
import { UspBarBlok, UspItemBlok } from "@/components/bloks/Usp";
import { RetailerListingSkeleton } from "@/features/retailers/RetailerListing";
import type { BlokTYPESCRIPTGENERATOR } from "@/storyblok";

export type AllBloks = NonNullable<BlokTYPESCRIPTGENERATOR["body"]>[number];

const NotFound = () => {
	return null;
};

defineStoryblok<AllBloks>({
	bloks: {
		/**
		 * Pages
		 */
		articleListingPage: NotFound,
		articlePage: ArticlePageBlok,
		page: PageBlok,
		productListingPage: NotFound,
		productPage: NotFound,
		/**
		 * Fetchers
		 */
		productGrid: {
			blok: ProductGridBlok,
			fetcher: rpc.bloks.products,
			getKey: ({ blok }) => blok.products,
			fallback: ProductGridBlokSkeleton,
		},
		products: {
			blok: ProductsBlok,
			fetcher: rpc.bloks.products,
			getKey: ({ blok }) => blok.products,
			fallback: ProductsSkeleton,
		},
		retailerListing: {
			blok: RetailerListingBlok,
			fetcher: async () => await rpc.retailers.list(),
			fallback: RetailerListingSkeleton,
		},
		/**
		 * Content types
		 */
		footer: NotFound,
		footerSection: NotFound,
		footerSocialLink: NotFound,
		globalConfig: NotFound,
		mobileNavigationSection: NotFound,
		navigation: NotFound,
		productStory: NotFound,
		retailer: NotFound,
		/**
		 * Bloks
		 */
		accordion: AccordionBlok,
		accordionGroupItem: AccordionGroupItemBlok,
		accordionGroups: AccordionGroupsBlok,
		accordionItem: AccordionItemBlok,
		articleCredits: ArticleCreditsBlok,
		button: ButtonBlok,
		campaignCard: CampaignCardBlok,
		carouselEndContent: CarouselEndContentBlok,
		categoryLink: NotFound,
		downloadLink: NotFound,
		hero1: HeroBlok1,
		hero2: HeroBlok2,
		hero3: HeroBlok3,
		hero4: HeroBlok4,
		heroFullVideo: HeroBlokFullVideo,
		embed: EmbedBlok,
		intro: IntroBlok,
		link: LinkBlok,
		manual: ManualBlok,
		manuals: ManualsBlok,
		manualSection: ManualSectionBlok,
		media: MediaBlok,
		mediaFrame: MediaFrameBlok,
		productSections: ProductSectionsBlok,
		qoute: QouteBlok,
		richText: RichTextBlok,
		rightOfWithdrawal: RightOfWithdrawalBlok,
		storyCard: StoryCardBlok,
		testimonial: TestimonialBlok,
		uspBar: UspBarBlok,
		uspItem: UspItemBlok,
		hubspotForm: NotFound,
		/**
		 * Keep at bottom
		 */
		TYPESCRIPT_GENERATOR: () => null,
	},
});
